import { FC, useEffect, useRef, useState } from 'react'

import clsx from 'clsx'

import { Slider, type SliderType } from 'shared/ui/slider'

import s from './styles.module.scss'

interface Props {
  images: string[]
  initialSlideIndex?: number
  isFullHeight?: boolean
}

const MAX_SLIDES_TO_SHOW = 3.5

const createThumbSettings = ({ imagesLength }: { imagesLength: number }) => ({
  slidesToShow: Math.min(imagesLength, MAX_SLIDES_TO_SHOW),
  swipeToSlide: true,
  dots: false,
  centerMode: false,
  infinite: false,
  focusOnSelect: true,
  arrows: false,
  className: s.thumbnailSlider,
})

export const ImageGallery: FC<Props> = ({
  images,
  initialSlideIndex = 0,
  isFullHeight = false,
}) => {
  const [nav1, setNav1] = useState<SliderType | null>(null)
  const [nav2, setNav2] = useState<SliderType | null>(null)
  const sliderRef1 = useRef<SliderType>(null)
  const sliderRef2 = useRef<SliderType>(null)
  const [currentSlideIndex, setCurrentSlideIndex] = useState(initialSlideIndex)

  const settingsMain = {
    swipeToSlide: true,
    arrows: false,
    dots: false,
    infinite: false,
    className: s.mainSlider,
    initialSlide: initialSlideIndex,
    beforeChange: (_: number, newIndex: number) => {
      setCurrentSlideIndex(newIndex)
    },
  }

  const settingsThumbs = {
    ...createThumbSettings({
      imagesLength: images.length,
    }),
    initialSlide: Math.max(
      0,
      initialSlideIndex -
        Math.floor(Math.min(images.length, MAX_SLIDES_TO_SHOW) / 2),
    ),
  }

  useEffect(() => {
    setNav1(sliderRef1.current)
    setNav2(sliderRef2.current)
  }, [])

  return (
    <div className={s.galleryContainer}>
      <Slider
        asNavFor={nav2 || undefined}
        ref={sliderRef1}
        {...settingsMain}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className={clsx(s.imageWrapper, {
              [s.fullHeightImage]: isFullHeight,
            })}
          >
            <img
              src={image}
              alt={`Gallery image ${index + 1}`}
            />
          </div>
        ))}
      </Slider>

      <Slider
        asNavFor={nav1 || undefined}
        ref={sliderRef2}
        {...settingsThumbs}
      >
        {images.map((image, index) => (
          <div
            key={index}
            className={clsx(s.thumbnailItem, {
              [s.active]: index === currentSlideIndex,
            })}
            onClick={() => {
              setCurrentSlideIndex(index)
            }}
          >
            <img
              src={image}
              alt={`Thumbnail ${index + 1}`}
            />
          </div>
        ))}
      </Slider>
    </div>
  )
}
